import { DEFAULT_VEHICLE_READ_RETENTION_DAYS, type PrivacySettings } from "@station/contracts";
import { Alert, Button, FormField, Input, SkeletonRows, StatusBadge, useToast } from "@station/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Section } from "../../../features/expenses/shared.js";
import {
  type FieldMessages,
  serverFieldErrors,
  translateErrors,
} from "../../../features/privacy/form-errors.js";
import {
  RETENTION_MAX,
  RETENTION_MIN,
  settingsBody,
  type SettingsFormField,
  type SettingsFormValues,
  validateSettingsForm,
} from "../../../features/privacy/privacy-values.js";
import { usePrivacyPermissions, usePrivacySettings } from "../../../features/privacy/queries.js";
import { PrivacyPage, Stamp } from "../../../features/privacy/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage, isApiError } from "../../../lib/errors.js";
import { formatCount } from "../../../lib/format.js";
import { queryKeys } from "../../../lib/query.js";

export const Route = createFileRoute("/_app/kvkk/ayarlar")({ component: PrivacySettingsRoute });

/**
 * KVKK ayarları (ADR 0017 §10): TTS / plaka tanıma okumalarının saklama süresi (dolunca worker FİZİKSEL olarak imha
 * eder; varsayılan 30 gün "doğrulanmalı") ve pazarlama SMS'inde görünen gönderici adı. Kayıt yoksa varsayılanlar
 * `isDefault` ile gelir; ilk kayıt `version: 0`. Yazma `privacy.manage` — yoksa salt okunur.
 */
function PrivacySettingsRoute(): ReactElement {
  const { t } = useTranslation();
  const settings = usePrivacySettings();
  const permissions = usePrivacyPermissions();
  return (
    <PrivacyPage title={t("menu.privacySettings")} description={t("privacy.settings.description")}>
      {!permissions.canRead ? null : settings.error ? (
        <Alert tone="danger">{errorMessage(settings.error)}</Alert>
      ) : !settings.data ? (
        <SkeletonRows rows={4} />
      ) : (
        <SettingsForm settings={settings.data} canManage={permissions.canManage} />
      )}
    </PrivacyPage>
  );
}

function formFrom(settings: PrivacySettings): SettingsFormValues {
  return {
    retentionDays: String(settings.vehicleReadRetentionDays),
    senderName: settings.marketingSenderName ?? "",
  };
}

const SERVER_FIELDS = {
  vehicleReadRetentionDays: "retentionDays",
  marketingSenderName: "senderName",
} as const;

function SettingsForm({
  settings,
  canManage,
}: {
  settings: PrivacySettings;
  canManage: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [values, setValues] = useState<SettingsFormValues>(() => formFrom(settings));
  const [errors, setErrors] = useState<FieldMessages<SettingsFormField>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const patch = (next: Partial<SettingsFormValues>) => setValues((current) => ({ ...current, ...next }));

  // Kayıt başka yerden değiştiyse (sürüm arttı) form sunucudaki değerlere döner.
  useEffect(() => {
    setValues(formFrom(settings));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.version]);

  const save = useMutation({
    mutationFn: async (form: SettingsFormValues) =>
      (await api.PUT("/privacy/settings", { body: settingsBody(form, settings.version) })).data!,
    onSuccess: async (saved) => {
      queryClient.setQueryData(queryKeys.privacy("settings"), saved);
      await queryClient.invalidateQueries({ queryKey: queryKeys.privacy("settings") });
      toast({ tone: "success", title: t("common.saved") });
    },
    onError: (caught) => {
      if (isApiError(caught, "VERSION_CONFLICT")) {
        setConflict(true);
        void queryClient.invalidateQueries({ queryKey: queryKeys.privacy("settings") });
        return;
      }
      const server = serverFieldErrors(
        caught,
        Object.keys(SERVER_FIELDS) as Array<keyof typeof SERVER_FIELDS>,
      );
      const mapped: FieldMessages<SettingsFormField> = {};
      for (const [field, message] of Object.entries(server.fields) as Array<
        [keyof typeof SERVER_FIELDS, string]
      >)
        mapped[SERVER_FIELDS[field]] = message;
      setErrors(mapped);
      if (server.unmatched || Object.keys(mapped).length === 0) setFormError(errorMessage(caught));
    },
  });

  return (
    <Section
      title={t("privacy.settings.title")}
      actions={
        settings.isDefault ? (
          <StatusBadge tone="warning" className="whitespace-nowrap">
            {t("privacy.settings.defaultBadge")}
          </StatusBadge>
        ) : (
          <span className="text-xs text-muted-foreground">
            {t("privacy.settings.updatedAt")}: <Stamp value={settings.updatedAt} />
          </span>
        )
      }
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setFormError(null);
          setConflict(false);
          const found = validateSettingsForm(values);
          setErrors(translateErrors(found, t));
          if (Object.keys(found).length === 0) save.mutate(values);
        }}
      >
        {!canManage ? <Alert tone="info">{t("privacy.settings.readOnly")}</Alert> : null}
        {conflict ? <Alert tone="warning">{t("privacy.settings.conflict")}</Alert> : null}
        {formError ? <Alert tone="danger">{formError}</Alert> : null}

        <div className="grid gap-4 md:grid-cols-2">
          <FormField
            label={t("privacy.settings.retentionDays")}
            description={t("privacy.settings.retentionHint", {
              min: formatCount(RETENTION_MIN),
              max: formatCount(RETENTION_MAX),
              default: DEFAULT_VEHICLE_READ_RETENTION_DAYS,
            })}
            error={errors.retentionDays}
            required
          >
            {(control) => (
              <Input
                {...control}
                inputMode="numeric"
                className="w-40 text-right tabular"
                maxLength={4}
                readOnly={!canManage}
                value={values.retentionDays}
                onChange={(event) => patch({ retentionDays: event.target.value })}
              />
            )}
          </FormField>
          <FormField
            label={t("privacy.settings.senderName")}
            description={t("privacy.settings.senderNameHint")}
            error={errors.senderName}
          >
            {(control) => (
              <Input
                {...control}
                maxLength={40}
                autoComplete="off"
                readOnly={!canManage}
                value={values.senderName}
                onChange={(event) => patch({ senderName: event.target.value })}
              />
            )}
          </FormField>
        </div>
        <Alert tone="warning" title={t("privacy.settings.verifyTitle")}>
          {t("privacy.settings.verifyBody")}
        </Alert>
        {canManage ? (
          <div className="flex justify-end">
            <Button type="submit" loading={save.isPending}>
              {t("common.save")}
            </Button>
          </div>
        ) : null}
      </form>
    </Section>
  );
}
